import { useEffect, useState } from "react";
import { toast } from "sonner";
import posthog from "posthog-js";
import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";

/**
 * Workspace AI-deal questions editor state, shared by the Settings page and
 * the in-deal "Add questions" dialog so the two stay in lockstep.
 */
export function useQuestionnaireEditor() {
  const { workspace, isLoading, activeWorkspaceId, updateWorkspaceMutation } =
    useWorkspaceSettings();

  const [questions, setQuestions] = useState<string[]>([]);

  useEffect(() => {
    if (workspace) setQuestions(workspace.questionnaire || []);
  }, [workspace]);

  const addQuestion = () => {
    if (questions.some((q) => q.trim() === "")) {
      toast.error("Please fill in the empty question before adding a new one");
      return;
    }
    setQuestions([...questions, ""]);
  };

  const removeQuestion = (index: number) =>
    setQuestions(questions.filter((_, i) => i !== index));

  const updateQuestion = (index: number, value: string) => {
    const next = [...questions];
    next[index] = value;
    setQuestions(next);
  };

  const isDirty =
    JSON.stringify(questions.filter((q) => q.trim() !== "")) !==
    JSON.stringify(workspace?.questionnaire || []);

  const save = (onSaved?: () => void) => {
    const filtered = questions.filter((q) => q.trim() !== "");
    const toastId = toast.loading("Saving...");
    updateWorkspaceMutation.mutate(
      { questionnaire: filtered },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
          toast.success("Questions saved");
          posthog.capture("settings_ai_deal_questions_updated", {
            workspace_id: activeWorkspaceId,
            question_count: filtered.length,
          });
          onSaved?.();
        },
        onError: () => {
          toast.dismiss(toastId);
        },
      },
    );
  };

  return {
    questions,
    isLoading,
    isDirty,
    isSaving: updateWorkspaceMutation.isPending,
    addQuestion,
    removeQuestion,
    updateQuestion,
    save,
  };
}
